// The one Library list (DES1 LIBRARY-IA): its search, kind-filter and empty-state words, and the three states a row can show.
// Namespace `library-feed`; owner T1-UI2. Values are DES2's library-ia-24.json and drafts-ready-24.json, verbatim. Kind names,
// segments, loading and the "Apps match" line reuse the shipped artifact-panels keys.
import type { NamespaceCatalog } from '@/i18n/ns';

const bg = {
  appsMatchNoun: 'one:приложение съвпада в Приложения →|other:приложения съвпадат в Приложения →',
  kindFiles: 'Файлове',
  search: 'Търсене в запазената работа…',
  emptyDocuments: 'Още няма документи.',
  emptyDecks: 'Още няма презентации.',
  emptySheets: 'Още няма електронни таблици.',
  emptyFiles: 'Още няма файлове.',
  emptyKindLine: 'Поискайте в чата и ще се появи тук.',
  emptyShared: 'Още нищо не е споделено с вас.',
  emptySharedLine: 'Когато колега сподели документ с вас, той ще се появи тук.',
  clearSearch: 'Изчистване на търсенето',
  stateDraft: 'Чернова',
  stateReady: 'Готово',
  stateChanged: 'Променено след „Готово“',
  description: 'Всичко, което сте запазили или получили. Качените файлове остават лични за вас.',
  indexing: 'Индексирането още не е приключило. Някои резултати може да липсват.',
  indexingRetry: 'Опитайте отново',
  owner: 'Собственик',
  ownerAnyone: 'Всеки',
  kindDocsShort: 'Документи',
  kindSheetsShort: 'Таблици',
  groupToday: 'Днес',
  groupYesterday: 'Вчера',
  groupEarlier: 'По-рано',
} as const;
const hr = {
  appsMatchNoun:
    'one:aplikacija odgovara u odjeljku Aplikacije →|few:aplikacije odgovaraju u odjeljku Aplikacije →|other:aplikacija odgovara u odjeljku Aplikacije →',
  kindFiles: 'Datoteke',
  search: 'Pretraži spremljeni rad…',
  emptyDocuments: 'Još nema dokumenata.',
  emptyDecks: 'Još nema prezentacija.',
  emptySheets: 'Još nema proračunskih tablica.',
  emptyFiles: 'Još nema datoteka.',
  emptyKindLine: 'Zatražite to u chatu i pojavit će se ovdje.',
  emptyShared: 'Još ništa nije podijeljeno s vama.',
  emptySharedLine: 'Kad kolega podijeli dokument s vama, pojavit će se ovdje.',
  clearSearch: 'Očisti pretraživanje',
  stateDraft: 'Nacrt',
  stateReady: 'Spremno',
  stateChanged: 'Promijenjeno nakon oznake Spremno',
  description: 'Sve što ste spremili ili primili. Prenesene datoteke ostaju privatne za vas.',
  indexing: 'Indeksiranje je još u tijeku. Neki rezultati možda nedostaju.',
  indexingRetry: 'Pokušajte ponovno',
  owner: 'Vlasnik',
  ownerAnyone: 'Bilo tko',
  kindDocsShort: 'Dokumenti',
  kindSheetsShort: 'Tablice',
  groupToday: 'Danas',
  groupYesterday: 'Jučer',
  groupEarlier: 'Ranije',
} as const;
const cs = {
  appsMatchNoun:
    'one:aplikace odpovídá v sekci Aplikace →|few:aplikace odpovídají v sekci Aplikace →|many:aplikace odpovídá v sekci Aplikace →|other:aplikací odpovídá v sekci Aplikace →',
  kindFiles: 'Soubory',
  search: 'Hledat v uložené práci…',
  emptyDocuments: 'Zatím žádné dokumenty.',
  emptyDecks: 'Zatím žádné prezentace.',
  emptySheets: 'Zatím žádné tabulky.',
  emptyFiles: 'Zatím žádné soubory.',
  emptyKindLine: 'Požádejte o to v chatu a objeví se tady.',
  emptyShared: 'Zatím s vámi nikdo nic nesdílel.',
  emptySharedLine: 'Když s vámi kolega nasdílí dokument, objeví se tady.',
  clearSearch: 'Vymazat hledání',
  stateDraft: 'Koncept',
  stateReady: 'Připraveno',
  stateChanged: 'Změněno od označení Připraveno',
  description: 'Vše, co jste uložili nebo dostali. Nahrané soubory zůstávají soukromé jen pro vás.',
  indexing: 'Indexování ještě probíhá. Některé výsledky mohou chybět.',
  indexingRetry: 'Zkusit znovu',
  owner: 'Vlastník',
  ownerAnyone: 'Kdokoli',
  kindDocsShort: 'Dokumenty',
  kindSheetsShort: 'Tabulky',
  groupToday: 'Dnes',
  groupYesterday: 'Včera',
  groupEarlier: 'Dříve',
} as const;
const da = {
  appsMatchNoun: 'one:app matcher i Apps →|other:apps matcher i Apps →',
  kindFiles: 'Filer',
  search: 'Søg i gemt arbejde…',
  emptyDocuments: 'Ingen dokumenter endnu.',
  emptyDecks: 'Ingen præsentationer endnu.',
  emptySheets: 'Ingen regneark endnu.',
  emptyFiles: 'Ingen filer endnu.',
  emptyKindLine: 'Bed om en i chatten, så havner den her.',
  emptyShared: 'Intet er delt med dig endnu.',
  emptySharedLine: 'Når en kollega deler et dokument med dig, vises det her.',
  clearSearch: 'Ryd søgning',
  stateDraft: 'Kladde',
  stateReady: 'Klar',
  stateChanged: 'Ændret siden klar',
  description: 'Alt, hvad du har gemt eller fået. Uploads forbliver private for dig.',
  indexing: 'Indekserer stadig. Nogle resultater kan mangle.',
  indexingRetry: 'Prøv igen',
  owner: 'Ejer',
  ownerAnyone: 'Alle',
  kindDocsShort: 'Dokumenter',
  kindSheetsShort: 'Regneark',
  groupToday: 'I dag',
  groupYesterday: 'I går',
  groupEarlier: 'Tidligere',
} as const;
const nl = {
  appsMatchNoun: 'one:app komt overeen in Apps →|other:apps komen overeen in Apps →',
  kindFiles: 'Bestanden',
  search: 'Opgeslagen werk zoeken…',
  emptyDocuments: 'Nog geen documenten.',
  emptyDecks: 'Nog geen presentaties.',
  emptySheets: 'Nog geen spreadsheets.',
  emptyFiles: 'Nog geen bestanden.',
  emptyKindLine: 'Vraag erom in de chat, dan komt het hier.',
  emptyShared: 'Er is nog niets met je gedeeld.',
  emptySharedLine: 'Als een collega een document met je deelt, verschijnt het hier.',
  clearSearch: 'Zoekopdracht wissen',
  stateDraft: 'Concept',
  stateReady: 'Klaar',
  stateChanged: 'Gewijzigd sinds klaar',
  description: 'Alles wat je hebt opgeslagen of gekregen. Uploads blijven privé voor jou.',
  indexing: 'Nog bezig met indexeren. Sommige resultaten kunnen ontbreken.',
  indexingRetry: 'Opnieuw proberen',
  owner: 'Eigenaar',
  ownerAnyone: 'Iedereen',
  kindDocsShort: 'Documenten',
  kindSheetsShort: 'Tabellen',
  groupToday: 'Vandaag',
  groupYesterday: 'Gisteren',
  groupEarlier: 'Eerder',
} as const;
const en = {
  appsMatchNoun: 'one:App matches in Apps →|other:Apps match in Apps →',
  kindFiles: 'Files',
  search: 'Search saved work…',
  emptyDocuments: 'No documents yet.',
  emptyDecks: 'No decks yet.',
  emptySheets: 'No sheets yet.',
  emptyFiles: 'No files yet.',
  emptyKindLine: 'Ask for one in chat, and it lands here.',
  emptyShared: 'Nothing shared with you yet.',
  emptySharedLine: 'When a colleague shares a document with you, it appears here.',
  clearSearch: 'Clear search',
  stateDraft: 'Draft',
  stateReady: 'Ready',
  stateChanged: 'Changed since ready',
  description: 'Everything you’ve saved or been given. Uploads stay private to you.',
  indexing: 'Still indexing. Some results may be missing.',
  indexingRetry: 'Retry',
  owner: 'Owner',
  ownerAnyone: 'Anyone',
  kindDocsShort: 'Docs',
  kindSheetsShort: 'Sheets',
  groupToday: 'Today',
  groupYesterday: 'Yesterday',
  groupEarlier: 'Earlier',
} as const;
const et = {
  appsMatchNoun:
    'one:rakendus sobib jaotises Rakendused →|other:rakendust sobivad jaotises Rakendused →',
  kindFiles: 'Failid',
  search: 'Otsi salvestatud tööst…',
  emptyDocuments: 'Dokumente veel pole.',
  emptyDecks: 'Esitlusi veel pole.',
  emptySheets: 'Tabeleid veel pole.',
  emptyFiles: 'Faile veel pole.',
  emptyKindLine: 'Küsi seda vestluses ja see jõuab siia.',
  emptyShared: 'Sinuga pole veel midagi jagatud.',
  emptySharedLine: 'Kui kolleeg jagab sinuga dokumenti, ilmub see siia.',
  clearSearch: 'Tühjenda otsing',
  stateDraft: 'Mustand',
  stateReady: 'Valmis',
  stateChanged: 'Muudetud pärast valmis märkimist',
  description: 'Kõik, mille oled salvestanud või saanud. Üleslaaditud failid jäävad ainult sulle.',
  indexing: 'Indekseerimine käib veel. Mõni tulemus võib puududa.',
  indexingRetry: 'Proovi uuesti',
  owner: 'Omanik',
  ownerAnyone: 'Kõik',
  kindDocsShort: 'Dokumendid',
  kindSheetsShort: 'Tabelid',
  groupToday: 'Täna',
  groupYesterday: 'Eile',
  groupEarlier: 'Varem',
} as const;
const fi = {
  appsMatchNoun:
    'one:sovellus vastaa hakua kohdassa Sovellukset →|other:sovellusta vastaa hakua kohdassa Sovellukset →',
  kindFiles: 'Tiedostot',
  search: 'Hae tallennetusta työstä…',
  emptyDocuments: 'Ei vielä asiakirjoja.',
  emptyDecks: 'Ei vielä esityksiä.',
  emptySheets: 'Ei vielä taulukkolaskentoja.',
  emptyFiles: 'Ei vielä tiedostoja.',
  emptyKindLine: 'Pyydä sitä chatissa, niin se tulee tänne.',
  emptyShared: 'Kanssasi ei ole vielä jaettu mitään.',
  emptySharedLine: 'Kun kollega jakaa kanssasi asiakirjan, se näkyy täällä.',
  clearSearch: 'Tyhjennä haku',
  stateDraft: 'Luonnos',
  stateReady: 'Valmis',
  stateChanged: 'Muutettu valmiiksi merkitsemisen jälkeen',
  description:
    'Kaikki, mitä olet tallentanut tai saanut. Lataamasi tiedostot näkyvät vain sinulle.',
  indexing: 'Indeksointi on vielä kesken. Joitakin tuloksia voi puuttua.',
  indexingRetry: 'Yritä uudelleen',
  owner: 'Omistaja',
  ownerAnyone: 'Kaikki',
  kindDocsShort: 'Asiakirjat',
  kindSheetsShort: 'Taulukot',
  groupToday: 'Tänään',
  groupYesterday: 'Eilen',
  groupEarlier: 'Aiemmin',
} as const;
const fr = {
  appsMatchNoun: 'one:app correspond dans Apps →|other:apps correspondent dans Apps →',
  kindFiles: 'Fichiers',
  search: 'Rechercher dans le travail enregistré…',
  emptyDocuments: 'Aucun document pour l’instant.',
  emptyDecks: 'Aucune présentation pour l’instant.',
  emptySheets: 'Aucune feuille de calcul pour l’instant.',
  emptyFiles: 'Aucun fichier pour l’instant.',
  emptyKindLine: 'Demandez-le dans le chat : il arrivera ici.',
  emptyShared: 'Rien n’a encore été partagé avec vous.',
  emptySharedLine: 'Quand un collègue partage un document avec vous, il apparaît ici.',
  clearSearch: 'Effacer la recherche',
  stateDraft: 'Brouillon',
  stateReady: 'Prêt',
  stateChanged: 'Modifié depuis « Prêt »',
  description: 'Tout ce que vous avez enregistré ou reçu. Vos importations restent privées.',
  indexing: 'Indexation en cours. Certains résultats peuvent manquer.',
  indexingRetry: 'Réessayer',
  owner: 'Propriétaire',
  ownerAnyone: 'Tous',
  kindDocsShort: 'Docs',
  kindSheetsShort: 'Feuilles',
  groupToday: 'Aujourd’hui',
  groupYesterday: 'Hier',
  groupEarlier: 'Plus tôt',
} as const;
const de = {
  appsMatchNoun: 'one:App passt in Apps →|other:Apps passen in Apps →',
  kindFiles: 'Dateien',
  search: 'Gespeicherte Arbeit durchsuchen…',
  emptyDocuments: 'Noch keine Dokumente.',
  emptyDecks: 'Noch keine Präsentationen.',
  emptySheets: 'Noch keine Tabellenkalkulationen.',
  emptyFiles: 'Noch keine Dateien.',
  emptyKindLine: 'Bitten Sie im Chat darum, dann landet es hier.',
  emptyShared: 'Noch nichts mit Ihnen geteilt.',
  emptySharedLine: 'Wenn jemand aus Ihrem Team ein Dokument mit Ihnen teilt, erscheint es hier.',
  clearSearch: 'Suche löschen',
  stateDraft: 'Entwurf',
  stateReady: 'Bereit',
  stateChanged: 'Seit „Bereit“ geändert',
  description:
    'Alles, was Sie gespeichert oder erhalten haben. Hochgeladene Dateien sind nur für Sie sichtbar.',
  indexing: 'Die Indexierung läuft noch. Einige Ergebnisse fehlen möglicherweise.',
  indexingRetry: 'Erneut versuchen',
  owner: 'Eigentümer',
  ownerAnyone: 'Alle',
  kindDocsShort: 'Dokumente',
  kindSheetsShort: 'Tabellen',
  groupToday: 'Heute',
  groupYesterday: 'Gestern',
  groupEarlier: 'Früher',
} as const;
const el = {
  appsMatchNoun:
    'one:εφαρμογή ταιριάζει σε: Εφαρμογές →|other:εφαρμογές ταιριάζουν σε: Εφαρμογές →',
  kindFiles: 'Αρχεία',
  search: 'Αναζήτηση στην αποθηκευμένη δουλειά…',
  emptyDocuments: 'Δεν υπάρχουν ακόμη έγγραφα.',
  emptyDecks: 'Δεν υπάρχουν ακόμη παρουσιάσεις.',
  emptySheets: 'Δεν υπάρχουν ακόμη υπολογιστικά φύλλα.',
  emptyFiles: 'Δεν υπάρχουν ακόμη αρχεία.',
  emptyKindLine: 'Ζητήστε το στη συνομιλία και θα εμφανιστεί εδώ.',
  emptyShared: 'Δεν έχει κοινοποιηθεί τίποτα μαζί σας ακόμη.',
  emptySharedLine: 'Όταν ένας συνάδελφος μοιραστεί ένα έγγραφο μαζί σας, θα εμφανιστεί εδώ.',
  clearSearch: 'Απαλοιφή αναζήτησης',
  stateDraft: 'Πρόχειρο',
  stateReady: 'Έτοιμο',
  stateChanged: 'Άλλαξε μετά τη σήμανση «Έτοιμο»',
  description: 'Ό,τι έχετε αποθηκεύσει ή λάβει. Οι μεταφορτώσεις σας παραμένουν ιδιωτικές.',
  indexing: 'Η ευρετηρίαση συνεχίζεται. Ορισμένα αποτελέσματα μπορεί να λείπουν.',
  indexingRetry: 'Δοκιμάστε ξανά',
  owner: 'Κάτοχος',
  ownerAnyone: 'Οποιοσδήποτε',
  kindDocsShort: 'Έγγραφα',
  kindSheetsShort: 'Φύλλα',
  groupToday: 'Σήμερα',
  groupYesterday: 'Χθες',
  groupEarlier: 'Νωρίτερα',
} as const;
const hu = {
  appsMatchNoun:
    'one:alkalmazás egyezik itt: Alkalmazások →|other:alkalmazás egyezik itt: Alkalmazások →',
  kindFiles: 'Fájlok',
  search: 'Keresés a mentett munkákban…',
  emptyDocuments: 'Még nincsenek dokumentumok.',
  emptyDecks: 'Még nincsenek prezentációk.',
  emptySheets: 'Még nincsenek táblázatok.',
  emptyFiles: 'Még nincsenek fájlok.',
  emptyKindLine: 'Kérje a csevegésben, és itt jelenik meg.',
  emptyShared: 'Még semmit sem osztottak meg Önnel.',
  emptySharedLine: 'Ha egy kolléga megoszt Önnel egy dokumentumot, itt jelenik meg.',
  clearSearch: 'Keresés törlése',
  stateDraft: 'Piszkozat',
  stateReady: 'Kész',
  stateChanged: 'Módosult a Kész jelölés óta',
  description: 'Minden, amit mentett vagy kapott. A feltöltései csak Ön számára láthatók.',
  indexing: 'Az indexelés még tart. Egyes találatok hiányozhatnak.',
  indexingRetry: 'Próbálja újra',
  owner: 'Tulajdonos',
  ownerAnyone: 'Bárki',
  kindDocsShort: 'Dokumentumok',
  kindSheetsShort: 'Táblázatok',
  groupToday: 'Ma',
  groupYesterday: 'Tegnap',
  groupEarlier: 'Korábban',
} as const;
const ga = {
  appsMatchNoun:
    'one:aip ag teacht leis in Aipeanna →|two:aip ag teacht leis in Aipeanna →|few:aip ag teacht leis in Aipeanna →|many:n-aip ag teacht leis in Aipeanna →|other:aip ag teacht leis in Aipeanna →',
  kindFiles: 'Comhaid',
  search: 'Cuardaigh obair shábháilte…',
  emptyDocuments: 'Níl aon doiciméid ann fós.',
  emptyDecks: 'Níl aon láithreoireachtaí ann fós.',
  emptySheets: 'Níl aon scarbhileoga ann fós.',
  emptyFiles: 'Níl aon chomhaid ann fós.',
  emptyKindLine: 'Iarr é sa chomhrá agus tiocfaidh sé anseo.',
  emptyShared: 'Níor roinneadh aon rud leat fós.',
  emptySharedLine: 'Nuair a roinneann comhghleacaí doiciméad leat, beidh sé anseo.',
  clearSearch: 'Glan an cuardach',
  stateDraft: 'Dréacht',
  stateReady: 'Réidh',
  stateChanged: 'Athraithe ó marcáladh réidh é',
  description: 'Gach rud a shábháil tú nó a tugadh duit. Fanann uaslódálacha príobháideach duit.',
  indexing: 'Fós ag innéacsú. D’fhéadfadh roinnt torthaí a bheith in easnamh.',
  indexingRetry: 'Bain triail eile as',
  owner: 'Úinéir',
  ownerAnyone: 'Duine ar bith',
  kindDocsShort: 'Doiciméid',
  kindSheetsShort: 'Bileoga',
  groupToday: 'Inniu',
  groupYesterday: 'Inné',
  groupEarlier: 'Níos luaithe',
} as const;
const it = {
  appsMatchNoun: 'one:App corrisponde in App →|other:App corrispondono in App →',
  kindFiles: 'File',
  search: 'Cerca nel lavoro salvato…',
  emptyDocuments: 'Ancora nessun documento.',
  emptyDecks: 'Ancora nessuna presentazione.',
  emptySheets: 'Ancora nessun foglio di calcolo.',
  emptyFiles: 'Ancora nessun file.',
  emptyKindLine: 'Chiedilo in chat e arriverà qui.',
  emptyShared: 'Nessuno ha ancora condiviso nulla con te.',
  emptySharedLine: 'Quando un collega condivide un documento con te, compare qui.',
  clearSearch: 'Cancella la ricerca',
  stateDraft: 'Bozza',
  stateReady: 'Pronto',
  stateChanged: 'Modificato da quando è pronto',
  description: 'Tutto ciò che hai salvato o ricevuto. I caricamenti restano privati per te.',
  indexing: 'Indicizzazione in corso. Alcuni risultati potrebbero mancare.',
  indexingRetry: 'Riprova',
  owner: 'Proprietario',
  ownerAnyone: 'Chiunque',
  kindDocsShort: 'Documenti',
  kindSheetsShort: 'Fogli',
  groupToday: 'Oggi',
  groupYesterday: 'Ieri',
  groupEarlier: 'In precedenza',
} as const;
const lv = {
  appsMatchNoun:
    'zero:lietotņu atbilst sadaļā Lietotnes →|one:lietotne atbilst sadaļā Lietotnes →|other:lietotnes atbilst sadaļā Lietotnes →',
  kindFiles: 'Faili',
  search: 'Meklēt saglabātajā darbā…',
  emptyDocuments: 'Vēl nav dokumentu.',
  emptyDecks: 'Vēl nav prezentāciju.',
  emptySheets: 'Vēl nav izklājlapu.',
  emptyFiles: 'Vēl nav failu.',
  emptyKindLine: 'Palūdziet to tērzēšanā, un tas parādīsies šeit.',
  emptyShared: 'Ar jums vēl nekas nav kopīgots.',
  emptySharedLine: 'Kad kolēģis ar jums kopīgos dokumentu, tas parādīsies šeit.',
  clearSearch: 'Notīrīt meklēšanu',
  stateDraft: 'Melnraksts',
  stateReady: 'Gatavs',
  stateChanged: 'Mainīts kopš atzīmes Gatavs',
  description:
    'Viss, kas saglabāts vai jums nodots. Augšupielādētie faili paliek privāti tikai jums.',
  indexing: 'Indeksēšana vēl notiek. Daži rezultāti var trūkt.',
  indexingRetry: 'Mēģināt vēlreiz',
  owner: 'Īpašnieks',
  ownerAnyone: 'Jebkurš',
  kindDocsShort: 'Dokumenti',
  kindSheetsShort: 'Tabulas',
  groupToday: 'Šodien',
  groupYesterday: 'Vakar',
  groupEarlier: 'Agrāk',
} as const;
const lt = {
  appsMatchNoun:
    'one:programėlė atitinka skiltyje Programėlės →|few:programėlės atitinka skiltyje Programėlės →|many:programėlės atitinka skiltyje Programėlės →|other:programėlių atitinka skiltyje Programėlės →',
  kindFiles: 'Failai',
  search: 'Ieškoti išsaugotuose darbuose…',
  emptyDocuments: 'Dokumentų dar nėra.',
  emptyDecks: 'Pristatymų dar nėra.',
  emptySheets: 'Skaičiuoklių dar nėra.',
  emptyFiles: 'Failų dar nėra.',
  emptyKindLine: 'Paprašykite pokalbyje ir tai atsiras čia.',
  emptyShared: 'Su jumis dar niekas nieko nebendrino.',
  emptySharedLine: 'Kai kolega su jumis pasidalins dokumentu, jis atsiras čia.',
  clearSearch: 'Išvalyti paiešką',
  stateDraft: 'Juodraštis',
  stateReady: 'Paruošta',
  stateChanged: 'Pakeista po pažymėjimo Paruošta',
  description: 'Viskas, ką išsaugojote ar gavote. Įkelti failai lieka privatūs tik jums.',
  indexing: 'Indeksavimas dar vyksta. Kai kurių rezultatų gali trūkti.',
  indexingRetry: 'Bandyti dar kartą',
  owner: 'Savininkas',
  ownerAnyone: 'Bet kas',
  kindDocsShort: 'Dokumentai',
  kindSheetsShort: 'Lentelės',
  groupToday: 'Šiandien',
  groupYesterday: 'Vakar',
  groupEarlier: 'Anksčiau',
} as const;
const mt = {
  appsMatchNoun:
    'one:app taqbel f’Apps →|two:apps jaqblu f’Apps →|few:apps jaqblu f’Apps →|many:app jaqblu f’Apps →|other:app jaqblu f’Apps →',
  kindFiles: 'Fajls',
  search: 'Fittex fix-xogħol salvat…',
  emptyDocuments: 'Għad m’hemmx dokumenti.',
  emptyDecks: 'Għad m’hemmx preżentazzjonijiet.',
  emptySheets: 'Għad m’hemmx folji tal-kalkolu.',
  emptyFiles: 'Għad m’hemmx fajls.',
  emptyKindLine: 'Itlobha fiċ-chat u tasal hawn.',
  emptyShared: 'Għadu ħadd ma qasam xejn miegħek.',
  emptySharedLine: 'Meta kollega jaqsam dokument miegħek, jidher hawn.',
  clearSearch: 'Neħħi t-tfittxija',
  stateDraft: 'Abbozz',
  stateReady: 'Lest',
  stateChanged: 'Inbidel minn meta mmarkat bħala lest',
  description: 'Kull ma salvajt jew ingħatajt. Il-fajls li ttella’ jibqgħu privati għalik.',
  indexing: 'Għadu qed jiġi indiċjat. Xi riżultati jistgħu jkunu nieqsa.',
  indexingRetry: 'Erġa’ pprova',
  owner: 'Sid',
  ownerAnyone: 'Kulħadd',
  kindDocsShort: 'Dokumenti',
  kindSheetsShort: 'Folji',
  groupToday: 'Illum',
  groupYesterday: 'Ilbieraħ',
  groupEarlier: 'Qabel',
} as const;
const pl = {
  appsMatchNoun:
    'one:aplikacja pasuje w sekcji Aplikacje →|few:aplikacje pasują w sekcji Aplikacje →|many:aplikacji pasuje w sekcji Aplikacje →|other:aplikacji pasuje w sekcji Aplikacje →',
  kindFiles: 'Pliki',
  search: 'Szukaj w zapisanych pracach…',
  emptyDocuments: 'Nie ma jeszcze dokumentów.',
  emptyDecks: 'Nie ma jeszcze prezentacji.',
  emptySheets: 'Nie ma jeszcze arkuszy.',
  emptyFiles: 'Nie ma jeszcze plików.',
  emptyKindLine: 'Poproś o to na czacie, a pojawi się tutaj.',
  emptyShared: 'Nikt jeszcze nic Ci nie udostępnił.',
  emptySharedLine: 'Gdy współpracownik udostępni Ci dokument, pojawi się on tutaj.',
  clearSearch: 'Wyczyść wyszukiwanie',
  stateDraft: 'Wersja robocza',
  stateReady: 'Gotowe',
  stateChanged: 'Zmieniono od oznaczenia jako gotowe',
  description: 'Wszystko, co zapisano lub Ci udostępniono. Przesłane pliki widzisz tylko Ty.',
  indexing: 'Indeksowanie wciąż trwa. Może brakować części wyników.',
  indexingRetry: 'Spróbuj ponownie',
  owner: 'Właściciel',
  ownerAnyone: 'Dowolny',
  kindDocsShort: 'Dokumenty',
  kindSheetsShort: 'Arkusze',
  groupToday: 'Dzisiaj',
  groupYesterday: 'Wczoraj',
  groupEarlier: 'Wcześniej',
} as const;
const pt = {
  appsMatchNoun:
    'one:App corresponde em Apps →|many:de Apps correspondem em Apps →|other:Apps correspondem em Apps →',
  kindFiles: 'Ficheiros',
  search: 'Pesquisar trabalho guardado…',
  emptyDocuments: 'Ainda não há documentos.',
  emptyDecks: 'Ainda não há apresentações.',
  emptySheets: 'Ainda não há folhas de cálculo.',
  emptyFiles: 'Ainda não há ficheiros.',
  emptyKindLine: 'Peça-o no chat e aparece aqui.',
  emptyShared: 'Ainda não foi partilhado nada consigo.',
  emptySharedLine: 'Quando um colega partilhar um documento consigo, ele aparece aqui.',
  clearSearch: 'Limpar pesquisa',
  stateDraft: 'Rascunho',
  stateReady: 'Pronto',
  stateChanged: 'Alterado desde Pronto',
  description: 'Tudo o que guardou ou recebeu. Os carregamentos ficam privados para si.',
  indexing: 'A indexação ainda está em curso. Podem faltar alguns resultados.',
  indexingRetry: 'Tentar novamente',
  owner: 'Proprietário',
  ownerAnyone: 'Qualquer pessoa',
  kindDocsShort: 'Documentos',
  kindSheetsShort: 'Folhas',
  groupToday: 'Hoje',
  groupYesterday: 'Ontem',
  groupEarlier: 'Anteriormente',
} as const;
const ro = {
  appsMatchNoun:
    'one:aplicație corespunde în Aplicații →|few:aplicații corespund în Aplicații →|other:de aplicații corespund în Aplicații →',
  kindFiles: 'Fișiere',
  search: 'Căutați în lucrările salvate…',
  emptyDocuments: 'Încă nu există documente.',
  emptyDecks: 'Încă nu există prezentări.',
  emptySheets: 'Încă nu există foi de calcul.',
  emptyFiles: 'Încă nu există fișiere.',
  emptyKindLine: 'Cereți-l în chat și va ajunge aici.',
  emptyShared: 'Încă nu s-a partajat nimic cu dumneavoastră.',
  emptySharedLine: 'Când un coleg partajează un document cu dumneavoastră, apare aici.',
  clearSearch: 'Ștergeți căutarea',
  stateDraft: 'Ciornă',
  stateReady: 'Gata',
  stateChanged: 'Modificat de la marcarea ca gata',
  description: 'Tot ce ați salvat sau primit. Încărcările rămân private pentru dvs.',
  indexing: 'Indexarea este încă în curs. Unele rezultate pot lipsi.',
  indexingRetry: 'Încercați din nou',
  owner: 'Proprietar',
  ownerAnyone: 'Oricine',
  kindDocsShort: 'Documente',
  kindSheetsShort: 'Foi',
  groupToday: 'Astăzi',
  groupYesterday: 'Ieri',
  groupEarlier: 'Mai devreme',
} as const;
const sk = {
  appsMatchNoun:
    'one:aplikácia zodpovedá v sekcii Aplikácie →|few:aplikácie zodpovedajú v sekcii Aplikácie →|many:aplikácie zodpovedá v sekcii Aplikácie →|other:aplikácií zodpovedá v sekcii Aplikácie →',
  kindFiles: 'Súbory',
  search: 'Hľadať v uloženej práci…',
  emptyDocuments: 'Zatiaľ žiadne dokumenty.',
  emptyDecks: 'Zatiaľ žiadne prezentácie.',
  emptySheets: 'Zatiaľ žiadne tabuľky.',
  emptyFiles: 'Zatiaľ žiadne súbory.',
  emptyKindLine: 'Požiadajte o to v chate a objaví sa tu.',
  emptyShared: 'Zatiaľ s vami nikto nič nezdieľal.',
  emptySharedLine: 'Keď s vami kolega zdieľa dokument, objaví sa tu.',
  clearSearch: 'Vymazať hľadanie',
  stateDraft: 'Koncept',
  stateReady: 'Pripravené',
  stateChanged: 'Zmenené od označenia Pripravené',
  description:
    'Všetko, čo ste uložili alebo dostali. Nahrané súbory zostávajú súkromné len pre vás.',
  indexing: 'Indexovanie stále prebieha. Niektoré výsledky môžu chýbať.',
  indexingRetry: 'Skúsiť znova',
  owner: 'Vlastník',
  ownerAnyone: 'Ktokoľvek',
  kindDocsShort: 'Dokumenty',
  kindSheetsShort: 'Tabuľky',
  groupToday: 'Dnes',
  groupYesterday: 'Včera',
  groupEarlier: 'Skôr',
} as const;
const sl = {
  appsMatchNoun:
    'one:aplikacija se ujema v razdelku Aplikacije →|two:aplikaciji se ujemata v razdelku Aplikacije →|few:aplikacije se ujemajo v razdelku Aplikacije →|other:aplikacij se ujema v razdelku Aplikacije →',
  kindFiles: 'Datoteke',
  search: 'Iskanje po shranjenem delu…',
  emptyDocuments: 'Še ni dokumentov.',
  emptyDecks: 'Še ni predstavitev.',
  emptySheets: 'Še ni preglednic.',
  emptyFiles: 'Še ni datotek.',
  emptyKindLine: 'Prosite zanj v klepetu in prikazal se bo tukaj.',
  emptyShared: 'Z vami še ni bilo nič deljeno.',
  emptySharedLine: 'Ko kolega z vami deli dokument, se prikaže tukaj.',
  clearSearch: 'Počisti iskanje',
  stateDraft: 'Osnutek',
  stateReady: 'Pripravljeno',
  stateChanged: 'Spremenjeno po oznaki Pripravljeno',
  description: 'Vse, kar ste shranili ali prejeli. Naložene datoteke ostanejo zasebne samo za vas.',
  indexing: 'Indeksiranje še poteka. Nekateri rezultati morda manjkajo.',
  indexingRetry: 'Poskusi znova',
  owner: 'Lastnik',
  ownerAnyone: 'Kdorkoli',
  kindDocsShort: 'Dokumenti',
  kindSheetsShort: 'Preglednice',
  groupToday: 'Danes',
  groupYesterday: 'Včeraj',
  groupEarlier: 'Prej',
} as const;
const es = {
  appsMatchNoun: 'one:app coincide en Apps →|other:apps coinciden en Apps →',
  kindFiles: 'Archivos',
  search: 'Buscar trabajo guardado…',
  emptyDocuments: 'Aún no hay documentos.',
  emptyDecks: 'Aún no hay presentaciones.',
  emptySheets: 'Aún no hay hojas de cálculo.',
  emptyFiles: 'Aún no hay archivos.',
  emptyKindLine: 'Pídelo en el chat y aparecerá aquí.',
  emptyShared: 'Aún no se ha compartido nada contigo.',
  emptySharedLine: 'Cuando un compañero comparta un documento contigo, aparecerá aquí.',
  clearSearch: 'Borrar búsqueda',
  stateDraft: 'Borrador',
  stateReady: 'Listo',
  stateChanged: 'Cambiado desde Listo',
  description: 'Todo lo que has guardado o recibido. Lo que subes es privado para ti.',
  indexing: 'Aún se está indexando. Puede que falten algunos resultados.',
  indexingRetry: 'Reintentar',
  owner: 'Propietario',
  ownerAnyone: 'Cualquiera',
  kindDocsShort: 'Documentos',
  kindSheetsShort: 'Hojas',
  groupToday: 'Hoy',
  groupYesterday: 'Ayer',
  groupEarlier: 'Antes',
} as const;
const sv = {
  appsMatchNoun: 'one:app matchar i Appar →|other:appar matchar i Appar →',
  kindFiles: 'Filer',
  search: 'Sök i sparat arbete…',
  emptyDocuments: 'Inga dokument ännu.',
  emptyDecks: 'Inga presentationer ännu.',
  emptySheets: 'Inga kalkylblad ännu.',
  emptyFiles: 'Inga filer ännu.',
  emptyKindLine: 'Be om en i chatten så hamnar den här.',
  emptyShared: 'Inget har delats med dig ännu.',
  emptySharedLine: 'När en kollega delar ett dokument med dig visas det här.',
  clearSearch: 'Rensa sökningen',
  stateDraft: 'Utkast',
  stateReady: 'Klart',
  stateChanged: 'Ändrad sedan klar',
  description: 'Allt du har sparat eller fått. Uppladdningar förblir privata för dig.',
  indexing: 'Indexerar fortfarande. Vissa resultat kan saknas.',
  indexingRetry: 'Försök igen',
  owner: 'Ägare',
  ownerAnyone: 'Alla',
  kindDocsShort: 'Dokument',
  kindSheetsShort: 'Kalkyl',
  groupToday: 'Idag',
  groupYesterday: 'Igår',
  groupEarlier: 'Tidigare',
} as const;

export type LibraryFeedCopyKey = keyof typeof en;
export const libraryFeedCopy: NamespaceCatalog<LibraryFeedCopyKey> = {
  bg,
  hr,
  cs,
  da,
  nl,
  en,
  et,
  fi,
  fr,
  de,
  el,
  hu,
  ga,
  it,
  lv,
  lt,
  mt,
  pl,
  pt,
  ro,
  sk,
  sl,
  es,
  sv,
};
export default libraryFeedCopy;
